import React, { useEffect, useRef, useState, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { MessageCircle, Loader2, CheckCircle2, Link2, Save, Unplug, ExternalLink, KeyRound, Copy, Webhook, FileText, Plus, Sparkles, Bot } from 'lucide-react';

const STARTER_TEMPLATES = [
  {
    label: 'Appointment reminder',
    name: 'appointment_reminder',
    category: 'UTILITY',
    language: 'en_US',
    body_text: 'Hi {{1}}, this is a reminder about your appointment with Helios on {{2}}. Reply here if you need to reschedule.',
    examples: ['John', 'Tuesday 14 March at 10am'],
  },
  {
    label: 'Install-day heads-up',
    name: 'install_day_heads_up',
    category: 'UTILITY',
    language: 'en_US',
    body_text: 'Hi {{1}}, your solar installation is booked for {{2}} and our team will arrive between {{3}}. Reply here with any questions.',
    examples: ['John', 'Tuesday 14 March', '8am and 9am'],
  },
];

export const WhatsAppConnect = () => {
  const [cfg, setCfg] = useState({ app_id: '', config_id: '', graph_version: 'v21.0', app_secret_set: false,
    connected: false, phone_number_id: '', waba_id: '', business_phone_number: '', verified_name: '' });
  const [appSecret, setAppSecret] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);
  const [manual, setManual] = useState({ phone_number_id: '', waba_id: '', access_token: '' });
  const [connectingManual, setConnectingManual] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tplError, setTplError] = useState('');
  const [tplLoading, setTplLoading] = useState(false);
  const [creatingTpl, setCreatingTpl] = useState(false);
  const [tplForm, setTplForm] = useState({ name: '', category: 'UTILITY', language: 'en_US', body_text: '', examples: '' });
  const [health, setHealth] = useState(null);
  const [checkingHealth, setCheckingHealth] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const sessionInfo = useRef({ phone_number_id: '', waba_id: '' });

  const [autoReply, setAutoReply] = useState({
    enabled: false, linked_enabled: true, linked_message: '',
    unlinked_enabled: true, unlinked_message: '',
  });
  const [autoReplyLoaded, setAutoReplyLoaded] = useState(false);
  const [savingAutoReply, setSavingAutoReply] = useState(false);

  const loadAutoReply = useCallback(async () => {
    try {
      const { data } = await api.get('/whatsapp/auto-reply');
      setAutoReply(data);
    } catch { /* ignore */ } finally { setAutoReplyLoaded(true); }
  }, []);

  const saveAutoReply = async () => {
    setSavingAutoReply(true);
    try {
      await api.put('/whatsapp/auto-reply', autoReply);
      toast.success('Automatic replies saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save automatic replies');
    } finally { setSavingAutoReply(false); }
  };

  const checkWebhookHealth = useCallback(async () => {
    setCheckingHealth(true);
    try {
      const r = await api.get('/whatsapp/webhook-health');
      setHealth(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not run diagnostics');
    } finally { setCheckingHealth(false); }
  }, []);

  const resubscribe = async () => {
    setSubscribing(true);
    try {
      const { data } = await api.post('/whatsapp/subscribe');
      if (data.app_field_enabled) {
        toast.success("Re-subscribed — 'messages' field enabled. Send a test reply to confirm.");
      } else if (data.app_field_enabled === false) {
        toast.warning(data.detail || "Re-subscribed, but couldn't auto-enable the 'messages' field — enable it in Meta → WhatsApp → Configuration.");
      } else {
        toast.success(data.detail || 'Re-subscribed to WhatsApp webhooks');
      }
      checkWebhookHealth();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not subscribe');
    } finally { setSubscribing(false); }
  };

  const loadTemplates = useCallback(async () => {
    setTplLoading(true);
    setTplError('');
    try {
      const r = await api.get('/whatsapp/templates', { params: { include_all: true } });
      setTemplates(r.data.templates || []);
      if (r.data.error) setTplError(r.data.error);
    } catch (e) {
      setTplError(e.response?.data?.detail || 'Could not load templates');
    } finally { setTplLoading(false); }
  }, []);

  const createTemplate = async (override) => {
    const data = override || tplForm;
    if (!data.name.trim() || !data.body_text.trim()) {
      toast.error('Give the template a name and message body.');
      return;
    }
    setCreatingTpl(true);
    try {
      const examples = (typeof data.examples === 'string'
        ? data.examples.split('\n')
        : (data.examples || [])).map((s) => s.trim()).filter(Boolean);
      const r = await api.post('/whatsapp/templates', {
        name: data.name, category: data.category, language: data.language || 'en_US',
        body_text: data.body_text, examples,
      });
      toast.success(`Template submitted (${r.data.status}). Meta will review it shortly.`);
      if (!override) setTplForm({ name: '', category: 'UTILITY', language: 'en_US', body_text: '', examples: '' });
      loadTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create template');
    } finally { setCreatingTpl(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/whatsapp/embedded-config');
      setCfg(r.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAutoReply(); }, [loadAutoReply]);
  useEffect(() => { if (cfg.connected) loadTemplates(); }, [cfg.connected, loadTemplates]);
  useEffect(() => { if (cfg.connected) checkWebhookHealth(); }, [cfg.connected, checkWebhookHealth]);

  // Load the Facebook JS SDK once an App ID is available.
  useEffect(() => {
    if (!cfg.app_id) return;
    if (window.FB) { setSdkReady(true); return; }
    window.fbAsyncInit = function () {
      window.FB.init({ appId: cfg.app_id, autoLogAppEvents: true, xfbml: true, version: cfg.graph_version || 'v21.0' });
      setSdkReady(true);
    };
    if (!document.getElementById('facebook-jssdk')) {
      const js = document.createElement('script');
      js.id = 'facebook-jssdk';
      js.src = 'https://connect.facebook.net/en_US/sdk.js';
      js.async = true; js.defer = true;
      document.body.appendChild(js);
    }
  }, [cfg.app_id, cfg.graph_version]);

  // Capture phone_number_id + waba_id emitted by the Embedded Signup popup.
  useEffect(() => {
    const onMessage = (event) => {
      if (!String(event.origin).includes('facebook.com')) return;
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data?.type === 'WA_EMBEDDED_SIGNUP') {
          if (data.data?.phone_number_id) sessionInfo.current.phone_number_id = data.data.phone_number_id;
          if (data.data?.waba_id) sessionInfo.current.waba_id = data.data.waba_id;
        }
      } catch { /* not a JSON message */ }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.post('/whatsapp/embedded-config', {
        app_id: cfg.app_id, config_id: cfg.config_id,
        app_secret: appSecret || (cfg.app_secret_set ? '***' : ''),
      });
      toast.success('WhatsApp config saved');
      setAppSecret('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save config');
    } finally { setSaving(false); }
  };

  const finish = async (code) => {
    try {
      const res = await api.post('/whatsapp/embedded-signup/exchange', {
        code,
        phone_number_id: sessionInfo.current.phone_number_id,
        waba_id: sessionInfo.current.waba_id,
      });
      toast.success(`WhatsApp connected${res.data.business_phone_number ? ' — ' + res.data.business_phone_number : ''}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Connection failed');
    } finally { setConnecting(false); }
  };

  const connect = () => {
    if (!window.FB || !cfg.config_id) {
      toast.error('Save your App ID, App Secret and Configuration ID first.');
      return;
    }
    setConnecting(true);
    sessionInfo.current = { phone_number_id: '', waba_id: '' };
    window.FB.login((response) => {
      const code = response?.authResponse?.code;
      if (code) { finish(code); }
      else { setConnecting(false); toast.info('WhatsApp connection cancelled'); }
    }, {
      config_id: cfg.config_id,
      response_type: 'code',
      override_default_response_type: true,
      extras: { setup: {}, featureType: 'whatsapp_business_app_onboarding', sessionInfoVersion: '3' },
    });
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect WhatsApp from the CRM? Your number stays on the WhatsApp Business app.')) return;
    try { await api.post('/whatsapp/disconnect'); toast.success('Disconnected'); await load(); }
    catch { toast.error('Could not disconnect'); }
  };

  const connectManual = async () => {
    if (!manual.phone_number_id.trim() || !manual.access_token.trim()) {
      toast.error('Enter the Phone Number ID and access token.');
      return;
    }
    setConnectingManual(true);
    try {
      const res = await api.post('/whatsapp/connect-manual', {
        phone_number_id: manual.phone_number_id.trim(),
        waba_id: manual.waba_id.trim(),
        access_token: manual.access_token.trim(),
      });
      toast.success(`WhatsApp connected${res.data.business_phone_number ? ' — ' + res.data.business_phone_number : ''}`);
      setManual({ phone_number_id: '', waba_id: '', access_token: '' });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Connection failed');
    } finally { setConnectingManual(false); }
  };

  const copy = async (v) => {
    try { await navigator.clipboard.writeText(v); toast.success('Copied'); } catch { toast.error('Copy failed'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="whatsapp-connect">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-green-600" /> WhatsApp Business (Coexistence)
          </CardTitle>
          <CardDescription>
            Connect your number so it works in the WhatsApp Business app <strong>and</strong> the CRM at the same time.
            You'll scan a QR code on your phone — your number stays active in the app.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {cfg.connected ? (
            <div className="rounded-lg border border-green-200 bg-green-50 p-4" data-testid="whatsapp-connected-banner">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-semibold text-slate-900">Connected</p>
                    <p className="text-sm text-slate-600">
                      {cfg.verified_name || 'WhatsApp Business'}{cfg.business_phone_number ? ` · ${cfg.business_phone_number}` : ''}
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={disconnect} data-testid="whatsapp-disconnect-btn">
                  <Unplug className="w-4 h-4 mr-1.5" /> Disconnect
                </Button>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm text-slate-600 mb-3">
                1) Enter your Meta credentials below and save. 2) Click <strong>Connect WhatsApp</strong> and scan the QR on your phone.
              </p>
              <Button
                onClick={connect}
                disabled={!cfg.config_id || !cfg.app_secret_set || connecting || !sdkReady}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="whatsapp-connect-btn"
              >
                {connecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
                Connect WhatsApp
              </Button>
              {(!cfg.config_id || !cfg.app_secret_set) && (
                <p className="text-xs text-amber-600 mt-2">Save your App ID, App Secret and Configuration ID first.</p>
              )}
              {!sdkReady && cfg.app_id && <p className="text-xs text-slate-400 mt-2">Loading Meta SDK…</p>}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Automatic replies — one-time responder on a customer's first-ever message */}
      <Card data-testid="whatsapp-auto-reply">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Bot className="w-4 h-4 text-emerald-600" /> Automatic replies
          </CardTitle>
          <CardDescription>
            Send an automatic WhatsApp reply the <strong>first time</strong> someone ever messages your number — with a different
            message for existing (linked) customers and new (non-linked) enquiries. Their message still shows as unread so your team follows up.
            Use <code>{'{name}'}</code> to insert the customer's first name.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between rounded-lg border bg-slate-50 p-3">
            <div>
              <p className="text-sm font-medium text-slate-800">Enable automatic replies</p>
              <p className="text-xs text-slate-500">Master switch — turn the whole responder on or off.</p>
            </div>
            <Switch checked={autoReply.enabled} onCheckedChange={(v) => setAutoReply({ ...autoReply, enabled: v })} data-testid="wa-autoreply-enabled" />
          </div>

          <div className={`space-y-4 transition-opacity ${autoReply.enabled ? '' : 'opacity-50 pointer-events-none'}`}>
            <div className="rounded-lg border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-800">Linked customers</p>
                  <p className="text-xs text-slate-500">People already in your CRM (matched by phone number).</p>
                </div>
                <Switch checked={autoReply.linked_enabled} onCheckedChange={(v) => setAutoReply({ ...autoReply, linked_enabled: v })} data-testid="wa-autoreply-linked-enabled" />
              </div>
              <Textarea
                rows={3}
                value={autoReply.linked_message}
                onChange={(e) => setAutoReply({ ...autoReply, linked_message: e.target.value })}
                placeholder="Hi {name}, thanks for messaging Helios — we've got your details and someone from the team will reply shortly."
                data-testid="wa-autoreply-linked-message"
              />
            </div>

            <div className="rounded-lg border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-800">Non-linked customers</p>
                  <p className="text-xs text-slate-500">New numbers not yet in your CRM (fresh enquiries).</p>
                </div>
                <Switch checked={autoReply.unlinked_enabled} onCheckedChange={(v) => setAutoReply({ ...autoReply, unlinked_enabled: v })} data-testid="wa-autoreply-unlinked-enabled" />
              </div>
              <Textarea
                rows={3}
                value={autoReply.unlinked_message}
                onChange={(e) => setAutoReply({ ...autoReply, unlinked_message: e.target.value })}
                placeholder="Thanks for contacting Helios Electrical & Solar! Could you share your name and postcode so we can help? A member of the team will be in touch soon."
                data-testid="wa-autoreply-unlinked-message"
              />
            </div>
          </div>

          <Button onClick={saveAutoReply} disabled={savingAutoReply || !autoReplyLoaded} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="wa-autoreply-save">
            {savingAutoReply ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save automatic replies
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Meta credentials</CardTitle>
          <CardDescription className="flex items-center gap-1">
            From your Meta Developer App.
            <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer"
               className="text-blue-600 inline-flex items-center gap-0.5">developers.facebook.com <ExternalLink className="w-3 h-3" /></a>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="wa-app-id">App ID</Label>
            <Input id="wa-app-id" data-testid="whatsapp-app-id" value={cfg.app_id || ''}
              onChange={(e) => setCfg({ ...cfg, app_id: e.target.value })} placeholder="e.g. 123456789012345" />
            <p className="text-xs text-slate-400 mt-1">App → Settings → Basic</p>
          </div>
          <div>
            <Label htmlFor="wa-config-id">Configuration ID</Label>
            <Input id="wa-config-id" data-testid="whatsapp-config-id" value={cfg.config_id || ''}
              onChange={(e) => setCfg({ ...cfg, config_id: e.target.value })} placeholder="Facebook Login for Business → Configurations" />
            <p className="text-xs text-slate-400 mt-1">Facebook Login for Business → Configurations (WhatsApp Embedded Signup)</p>
          </div>
          <div>
            <Label htmlFor="wa-app-secret">App Secret</Label>
            <Input id="wa-app-secret" data-testid="whatsapp-app-secret" type="password"
              value={appSecret}
              onChange={(e) => setAppSecret(e.target.value)}
              placeholder={cfg.app_secret_set ? '•••••••• (saved — leave blank to keep)' : 'App → Settings → Basic'} />
          </div>
          <Button onClick={saveConfig} disabled={saving} data-testid="whatsapp-save-config-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save credentials
          </Button>
        </CardContent>
      </Card>

      {/* Manual connect — paste Phone Number ID + WABA ID + permanent token */}
      {!cfg.connected && (
        <Card data-testid="whatsapp-manual-connect">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-sky-600" /> Connect manually (paste credentials)
            </CardTitle>
            <CardDescription>
              Already have your <strong>Phone Number ID</strong>, <strong>WhatsApp Business Account (WABA) ID</strong> and a
              <strong> permanent access token</strong>? Paste them here to connect instantly — no QR scan needed. (Your number stays live in the WhatsApp app.)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="wa-pnid">Phone Number ID</Label>
              <Input id="wa-pnid" data-testid="whatsapp-manual-pnid" value={manual.phone_number_id}
                onChange={(e) => setManual({ ...manual, phone_number_id: e.target.value })} placeholder="e.g. 123456789012345" />
              <p className="text-xs text-slate-400 mt-1">Meta → WhatsApp → API Setup</p>
            </div>
            <div>
              <Label htmlFor="wa-wabaid">WhatsApp Business Account (WABA) ID</Label>
              <Input id="wa-wabaid" data-testid="whatsapp-manual-wabaid" value={manual.waba_id}
                onChange={(e) => setManual({ ...manual, waba_id: e.target.value })} placeholder="Needed to send templates" />
            </div>
            <div>
              <Label htmlFor="wa-token">Permanent access token</Label>
              <Input id="wa-token" data-testid="whatsapp-manual-token" type="password" value={manual.access_token}
                onChange={(e) => setManual({ ...manual, access_token: e.target.value })} placeholder="System User permanent token" />
              <p className="text-xs text-slate-400 mt-1">Business Settings → Users → System Users → Generate token (whatsapp_business_messaging + whatsapp_business_management)</p>
            </div>
            <Button onClick={connectManual} disabled={connectingManual}
              className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="whatsapp-manual-connect-btn">
              {connectingManual ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
              Connect with these credentials
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Inbound message diagnostics */}
      {cfg.connected && (
        <Card data-testid="whatsapp-inbound-health">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Webhook className="w-4 h-4 text-indigo-600" /> Inbound message diagnostics
            </CardTitle>
            <CardDescription>Not receiving messages in the CRM inbox? Run this to see if Meta is reaching the webhook and whether the number is subscribed for messages.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <Button variant="outline" onClick={checkWebhookHealth} disabled={checkingHealth} data-testid="whatsapp-health-check-btn">
                {checkingHealth ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Webhook className="w-4 h-4 mr-2" />}
                Run diagnostics
              </Button>
              <Button onClick={resubscribe} disabled={subscribing} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="whatsapp-resubscribe-btn">
                {subscribing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
                Re-subscribe webhook
              </Button>
            </div>
            {health && (
              <div className="space-y-2" data-testid="whatsapp-health-result">
                <div className={`rounded-lg border p-3 text-sm ${
                  health.last_message_at ? 'bg-green-50 border-green-200 text-green-800'
                  : health.total_hits ? 'bg-amber-50 border-amber-200 text-amber-800'
                  : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
                  {health.verdict}
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="rounded-md bg-slate-50 border p-2"><p className="text-slate-400">Total webhook hits</p><p className="font-semibold text-slate-700">{health.total_hits}</p></div>
                  <div className="rounded-md bg-slate-50 border p-2"><p className="text-slate-400">Last inbound message</p><p className="font-semibold text-slate-700">{health.last_message_at ? new Date(health.last_message_at).toLocaleString('en-GB') : '—'}</p></div>
                  <div className="rounded-md bg-slate-50 border p-2"><p className="text-slate-400">WABA subscribed to app</p><p className={`font-semibold ${health.waba_subscribed_to_app ? 'text-green-600' : 'text-rose-600'}`}>{health.waba_subscribed_to_app === null ? 'unknown' : health.waba_subscribed_to_app ? 'yes' : 'no'}</p></div>
                  <div className="rounded-md bg-slate-50 border p-2"><p className="text-slate-400">'messages' field subscribed</p><p className={`font-semibold ${health.messages_field_subscribed ? 'text-green-600' : 'text-rose-600'}`}>{health.messages_field_subscribed ? 'yes' : 'no'}</p></div>
                </div>
                <p className="text-[11px] text-slate-400 break-all">Callback URL Meta must reach: <code className="text-slate-600">{health.callback_url}</code></p>
                {Array.isArray(health.subscribed_apps_detail) && health.subscribed_apps_detail.length > 0 && (
                  <div className="rounded-md bg-slate-50 border p-2 text-[11px] space-y-1" data-testid="whatsapp-subscribed-apps-detail">
                    <p className="text-slate-400">Apps subscribed to this WABA (from Meta):</p>
                    {health.subscribed_apps_detail.map((a, i) => (
                      <div key={i} className="text-slate-600">
                        <span className="font-medium">{a.app_name || a.app_id || 'app'}</span>
                        {a.app_id ? <span className="text-slate-400"> · {a.app_id}</span> : null}
                        <span className="text-slate-400"> · fields: </span>
                        <span className={a.fields?.includes('messages') ? 'text-green-600 font-medium' : 'text-rose-600'}>
                          {a.fields && a.fields.length ? a.fields.join(', ') : 'none'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {health.subscription_error && <p className="text-xs text-rose-600">Subscription check error: {health.subscription_error}</p>}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Webhook setup — needed for inbound messages to reach the CRM */}
      <Card data-testid="whatsapp-webhook-info">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Webhook className="w-4 h-4 text-indigo-600" /> Inbound webhook setup
          </CardTitle>
          <CardDescription>
            So customer messages reach the CRM, add this webhook in Meta → your App → WhatsApp → Configuration, and subscribe to the <strong>messages</strong> field.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs text-slate-500">Callback URL</Label>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-slate-50 border rounded-md px-2 py-1.5 break-all text-slate-700" data-testid="whatsapp-webhook-url">{cfg.webhook_callback_url}</code>
              <Button variant="outline" size="sm" onClick={() => copy(cfg.webhook_callback_url)}><Copy className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Verify token</Label>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-slate-50 border rounded-md px-2 py-1.5 break-all text-slate-700" data-testid="whatsapp-verify-token">{cfg.webhook_verify_token}</code>
              <Button variant="outline" size="sm" onClick={() => copy(cfg.webhook_verify_token)}><Copy className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Message templates — create & track approval */}
      {cfg.connected && (
        <Card data-testid="whatsapp-templates">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4 text-emerald-600" /> Message templates
            </CardTitle>
            <CardDescription>
              Templates let staff start a chat outside the 24-hour window. Create one here — Meta reviews it (usually minutes to a few hours) and it becomes usable once <strong>Approved</strong>.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-3 space-y-2" data-testid="wa-starter-templates">
              <p className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-emerald-600" /> Quick-start templates
              </p>
              <p className="text-xs text-slate-500">Ready-made drafts — submit to Meta for approval with one click, or load one into the form below to tweak first.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {STARTER_TEMPLATES.map((s) => (
                  <div key={s.name} className="rounded-lg border bg-white p-2.5">
                    <p className="text-sm font-medium text-slate-800">{s.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{s.body_text}</p>
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs"
                        disabled={creatingTpl} onClick={() => createTemplate(s)} data-testid={`wa-starter-submit-${s.name}`}>
                        {creatingTpl ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
                        Submit
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs"
                        onClick={() => setTplForm({ ...s, examples: s.examples.join('\n') })} data-testid={`wa-starter-load-${s.name}`}>
                        Load &amp; edit
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Name</Label>
                <Input value={tplForm.name} onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })}
                  placeholder="appointment_reminder" data-testid="wa-tpl-name" />
                <p className="text-[11px] text-slate-400 mt-1">lowercase, numbers, underscores</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Category</Label>
                <select value={tplForm.category} onChange={(e) => setTplForm({ ...tplForm, category: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="wa-tpl-category">
                  <option value="UTILITY">Utility (reminders, updates)</option>
                  <option value="MARKETING">Marketing (promotions)</option>
                  <option value="AUTHENTICATION">Authentication (codes)</option>
                </select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Language</Label>
                <Input value={tplForm.language} onChange={(e) => setTplForm({ ...tplForm, language: e.target.value })}
                  placeholder="en_US" data-testid="wa-tpl-language" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Message body</Label>
              <Textarea rows={3} value={tplForm.body_text} onChange={(e) => setTplForm({ ...tplForm, body_text: e.target.value })}
                placeholder="Hi {{1}}, this is a reminder about your solar installation on {{2}}." data-testid="wa-tpl-body" />
              <p className="text-[11px] text-slate-400 mt-1">Use {'{{1}}'}, {'{{2}}'} for values you fill in when sending.</p>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Example values (one per line, in order — only if you used variables)</Label>
              <Textarea rows={2} value={tplForm.examples} onChange={(e) => setTplForm({ ...tplForm, examples: e.target.value })}
                placeholder={"John\nTuesday 14 March"} data-testid="wa-tpl-examples" />
            </div>
            <Button onClick={createTemplate} disabled={creatingTpl} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="wa-tpl-create-btn">
              {creatingTpl ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Submit for approval
            </Button>

            <div className="pt-3 border-t border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-slate-700">Your templates</p>
                <Button variant="ghost" size="sm" onClick={loadTemplates} disabled={tplLoading} data-testid="wa-tpl-refresh">
                  {tplLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
                </Button>
              </div>
              {tplError ? (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">{tplError}</div>
              ) : templates.length === 0 ? (
                <p className="text-sm text-slate-400">No templates yet. Create one above.</p>
              ) : (
                <div className="space-y-2" data-testid="wa-tpl-list">
                  {templates.map((t) => (
                    <div key={`${t.name}-${t.language}`} className="flex items-start justify-between gap-2 p-2.5 rounded-lg border">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{t.name} <span className="text-xs text-slate-400">· {t.language} · {t.category}</span></p>
                        {t.body_text && <p className="text-xs text-slate-500 truncate">{t.body_text}</p>}
                      </div>
                      <Badge className={
                        t.status === 'APPROVED' ? 'bg-green-100 text-green-700'
                        : t.status === 'REJECTED' ? 'bg-rose-100 text-rose-700'
                        : 'bg-amber-100 text-amber-700'
                      }>{t.status || 'PENDING'}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
